/**
 * One question on the public form (`specs/M4-V8-forms.md` §6, acceptance P2).
 *
 * The control a kind renders is the control monday renders, and nothing more: a kind whose
 * column type we cannot write yet (email, phone, link, …) still renders and still validates —
 * the answer is parked server-side rather than dropped (q297).
 */
import type { FormQuestion } from '@elizmonday/shared';
import { useState } from 'react';

export function QuestionField({
  question,
  value,
  error,
  limit,
  onChange,
}: {
  question: FormQuestion;
  value: unknown;
  error: string | null;
  limit: number | null;
  onChange(next: unknown): void;
  /**
   * Declared so the **call site** may state the id this field renders — the coverage ledger reads
   * literal `data-testid` attributes off JSX and a component invocation carrying only handlers
   * was an element nothing could name (P-189). The value is the field's own
   * `question-<id>`; it is not read here, because the root already renders it.
   */
  'data-testid'?: string;
}) {
  const id = `${question.id}-input`;
  const text = typeof value === 'string' ? value : '';
  const invalid = error !== null;
  /** P-034: the counter is a long-text, while-focused affordance — see the foot below. */
  const [focused, setFocused] = useState(false);
  const showCount = limit !== null && question.kind === 'long_text' && focused;
  const common = {
    id,
    /*
     * **P-189.** The eight controls the kinds render all shared one `id` and carried no
     * `data-testid`, so `tools/qa/lib/inventory-parse.mjs#refMatches` could not count a single
     * one of them however many verifiers drove them. One id on `common` covers all eight,
     * which is the point: a new question kind inherits it rather than needing a new attribute.
     */
    'data-testid': `question-input-${question.id}`,
    'aria-invalid': invalid || undefined,
    'aria-describedby': invalid ? `${question.id}-error` : undefined,
    className: 'ez-form-input',
    onFocus: () => setFocused(true),
    onBlur: () => setFocused(false),
  } as const;

  if (question.kind === 'page_block') return null;
  if (question.kind === 'display_text') {
    return (
      <section className="ez-form-question" data-testid={`question-${question.id}`}>
        <p className="ez-form-question__block">{question.description ?? question.title}</p>
      </section>
    );
  }

  return (
    <section className="ez-form-question" data-testid={`question-${question.id}`} data-kind={question.kind}>
      <h2 className="ez-form-question__label">
        <label htmlFor={id}>{question.title}</label>
        {question.required ? (
          <span className="ez-form-question__required" aria-hidden="true" data-testid={`required-${question.id}`}>
            *
          </span>
        ) : null}
      </h2>
      {question.description !== null && question.description !== '' ? (
        <p className="ez-form-question__hint">{question.description}</p>
      ) : null}

      {renderControl()}

      {/*
        **P-034.** The foot exists only when it has something in it, and a character counter is
        not "something" on a short field: monday shows **no** counter under a text, email, phone
        or number question — ours printed `0/255` under every one of them, which is also the
        22 px that made the label-to-label pitch 122 where monday's is 100
        (`discovery/ui-reference/parity/1802997440/view-37628439-form-{monday,ours}.png`).
        monday counts only a **long text** answer, and only while the field has focus, so that
        is the only case that reserves the row.
      */}
      {error !== null || showCount ? (
        <div className="ez-form-question__foot">
          {error !== null ? (
            <span className="ez-form-question__error" id={`${question.id}-error`} data-testid={`error-${question.id}`}>
              {error}
            </span>
          ) : (
            <span />
          )}
          {showCount ? (
            <span className="ez-form-question__count" data-testid={`count-${question.id}`}>
              {text.length}/{limit}
            </span>
          ) : null}
        </div>
      ) : null}
    </section>
  );

  function renderControl() {
    switch (question.kind) {
      case 'long_text':
        return (
          <textarea
            {...common}
            data-testid={`question-input-${question.id}`}
            className="ez-form-input ez-form-input--long"
            value={text}
            maxLength={limit ?? undefined}
            onChange={(event) => onChange(event.target.value)}
          />
        );
      case 'number':
      case 'rating':
        return (
          <input
            {...common}
            data-testid={`question-input-${question.id}`}
            type="number"
            value={typeof value === 'number' || typeof value === 'string' ? String(value) : ''}
            onChange={(event) => onChange(event.target.value)}
          />
        );
      case 'boolean':
        return (
          <label className="ez-form-check">
            <input
              id={id}
              data-testid={`question-input-${question.id}`}
              type="checkbox"
              checked={value === true}
              onChange={(event) => onChange(event.target.checked)}
            />
            <span>{question.title}</span>
          </label>
        );
      case 'date':
        return (
          <input
            {...common}
            data-testid={`question-input-${question.id}`}
            type="date"
            value={text}
            onChange={(event) => onChange(event.target.value)}
          />
        );
      case 'email':
        return (
          <input
            {...common}
            data-testid={`question-input-${question.id}`}
            type="email"
            value={text}
            onChange={(event) => onChange(event.target.value)}
          />
        );
      case 'single_select':
        return (
          <select
            {...common}
            data-testid={`question-input-${question.id}`}
            value={text}
            onChange={(event) => onChange(event.target.value)}
          >
            <option value="" />
            {question.options.map((option) => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </select>
        );
      case 'multi_select':
        return (
          <select
            {...common}
            data-testid={`question-input-${question.id}`}
            multiple
            value={Array.isArray(value) ? (value as string[]) : []}
            onChange={(event) =>
              onChange([...event.target.selectedOptions].map((option) => option.value))
            }
          >
            {question.options.map((option) => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </select>
        );
      default:
        return (
          <input
            {...common}
            data-testid={`question-input-${question.id}`}
            type="text"
            value={text}
            maxLength={limit ?? undefined}
            onChange={(event) => onChange(event.target.value)}
          />
        );
    }
  }
}
